import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MODULE_ICONS } from "../../../components/icons/ModuleIcons";
import "../../../styles/np-theme.css";
import { FileText, BookOpen, FileCheck, Shield, Award } from "lucide-react";
import KnowledgeUpload from "./KnowledgeUpload";
import { useSiteDisplayName } from "../../../utils/siteUtils";

type DocumentType = 'selector' | 'user-manual' | 'slds' | 'knowledge' | 'contracts' | 'warranty';

interface DocumentOption {
  id: DocumentType;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const DocumentationPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const siteDisplayName = useSiteDisplayName(site);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeDocument, setActiveDocument] = useState<DocumentType>('selector');

  // Check for type query parameter and set active document accordingly
  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'user-manual' || type === 'slds' || type === 'knowledge' || type === 'contracts' || type === 'warranty') {
      setActiveDocument(type);
    } else {
      setActiveDocument('selector');
    }
  }, [searchParams]);

  const documentOptions: DocumentOption[] = [
    {
      id: 'user-manual',
      title: 'User Manual',
      description: 'Comprehensive user guides and operation manuals for all equipment',
      icon: <BookOpen size={20} />,
      color: 'blue'
    },
    {
      id: 'slds',
      title: 'SLDs',
      description: 'Single Line Diagrams and electrical schematics',
      icon: <FileText size={20} />,
      color: 'emerald'
    },
    {
      id: 'knowledge',
      title: 'Knowledge Base',
      description: 'Technical documents, procedures, and training materials',
      icon: <FileCheck size={20} />,
      color: 'purple'
    },
    {
      id: 'contracts',
      title: 'Contracts',
      description: 'Vendor contracts, agreements, and legal documents',
      icon: <Shield size={20} />,
      color: 'orange'
    },
    {
      id: 'warranty',
      title: 'Warranty Certificates',
      description: 'Equipment warranties and guarantee certificates',
      icon: <Award size={20} />,
      color: 'red'
    }
  ];

  // Selection lives in the URL (?type=…), so the sidebar links, the browser Back button and
  // shared links all land on the same category.
  const openDocument = (type: DocumentType) => {
    if (type === 'selector') {
      setSearchParams({});
    } else {
      setSearchParams({ type });
    }
  };

  // Render the Upload component for any selected document type
  if (activeDocument !== 'selector') {
    const selectedDoc = documentOptions.find(doc => doc.id === activeDocument);

    // Map document types to categories
    const getCategoryForType = (type: DocumentType): string => {
      switch (type) {
        case 'user-manual': return 'Manuals';
        case 'slds': return 'Diagrams';
        case 'contracts': return 'Contracts';
        case 'warranty': return 'Warranties';
        case 'knowledge': return 'all';
        default: return 'Other';
      }
    };

    return (
      <KnowledgeUpload
        key={activeDocument}
        title={selectedDoc?.title}
        description={selectedDoc?.description}
        defaultCategory={getCategoryForType(activeDocument)}
        onBack={() => openDocument('selector')}
        icon={selectedDoc?.icon}
        color={selectedDoc?.color}
      />
    );
  }

  const DocumentationIcon = MODULE_ICONS.documentation;

  // Document selector view
  return (
    <div className="np-page min-h-screen">
      <div className="np-theme max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        <div className="pb-5 border-b border-slate-200">
          <div className="np-pagehead">
            <div className="np-pagehead__mark">
              <DocumentationIcon size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="np-pagehead__title">Documentation</h1>
              <div className="np-pagehead__meta">
                <span className="np-pagehead__site">{siteDisplayName || site || 'SITE'}</span>
                <span className="np-pagehead__desc">Manuals, diagrams, contracts and technical resources</span>
              </div>
            </div>
          </div>
        </div>

        <h2 className="mt-6 mb-3">Choose a category</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {documentOptions.map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => openDocument(doc.id)}
              className="group np-kpi text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            >
              <div className="np-kpi__head">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded border border-blue-100 bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:border-blue-600 group-hover:text-white">
                  {doc.icon}
                </span>
              </div>
              <span className="mt-3 text-base text-slate-900 np-kpi__title">{doc.title}</span>
              <span className="np-kpi__hint">{doc.description}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
